import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Box, Button, CircularProgress, Typography, Paper } from '@mui/material';

// Set up the worker for PDF.js - using local file to avoid CORS issues
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentPreviewProps {
  file: File | string; // Can accept File object or URL string
  width?: number;
}

/**
 * A component to preview PDF documents
 */
const DocumentPreview: React.FC<DocumentPreviewProps> = ({ file, width = 300 }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load document');
    setLoading(false);
  };

  const changePage = (offset: number) => {
    if (!numPages) return;
    
    const newPage = pageNumber + offset;
    if (newPage >= 1 && newPage <= numPages) {
      setPageNumber(newPage);
    }
  };

  // Convert File to object URL if needed
  const fileUrl = typeof file === 'string' ? file : URL.createObjectURL(file);

  return (
    <Paper elevation={2} sx={{ p: 2, maxWidth: width + 40, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={40} />
          </Box>
        )}
        
        {error && (
          <Typography color="error" sx={{ my: 2 }}>
            {error}
          </Typography>
        )}
        
        {/* Wrap Document in a div to avoid nested button issues */}
        <div>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<CircularProgress size={40} />}
          >
            <Page 
              pageNumber={pageNumber} 
              width={width}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        </div>
        
        {numPages && numPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mt: 2 }}>
            <div>
              <Button 
                onClick={() => changePage(-1)} 
                disabled={pageNumber <= 1}
                variant="outlined"
                size="small"
              >
                Previous
              </Button>
            </div>
            
            <Typography variant="body2">
              Page {pageNumber} of {numPages}
            </Typography>
            
            <div>
              <Button 
                onClick={() => changePage(1)} 
                disabled={pageNumber >= numPages}
                variant="outlined"
                size="small"
              >
                Next
              </Button>
            </div>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DocumentPreview;